import { buildApiUrl } from "../../../config.js";
import React, { useEffect, useState } from "react";
import MobileOrderCard from "./MobileOrderCard";
import { ToastContainer, toast } from "react-toastify"; // Import toast notifications
import "react-toastify/dist/ReactToastify.css"; // Import toast CSS
import { FaEdit, FaTrash, FaShippingFast, FaFilePdf } from "react-icons/fa";
import EditOrderModal from "./EditOrderModal";
import ForClientOrderModal from "./ForClientOrderModal";
import Pagination from "./Pagination";
import SearchBar from "./SearchBar";
import TptUpdateModal from "./TptUpdateModal";
import { useNavigate } from "react-router-dom";
import { Search, Plus, FileText } from "lucide-react";
import { capitalizeWords } from "../../ui/capitalizeWords";
import Swal from "sweetalert2";
import { ColorRing, InfinitySpin } from "react-loader-spinner";
import convertToCr from "../../ui/convertToCr";

const SalesOrdersTable = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedItems, setExpandedItems] = useState({});
  const [selectedTab, setSelectedTab] = useState("all"); // Track selected tab
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [orderToEdit, setOrderToEdit] = useState(null); // Order data to be edited
  const token = localStorage.getItem("token");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const ordersPerPage = 50;
  const userRole = localStorage.getItem("role");
  const [isTptModalOpen, setIsTptModalOpen] = useState(false); // State to manage TPT modal visibility
  const [orderToUpdateTpt, setOrderToUpdateTpt] = useState(null);

  // Function to open TPT update modal
  const openTptUpdateModal = (orderId) => {
    setOrderToUpdateTpt(orderId);
    setIsTptModalOpen(true);
  };

  const closeTptUpdateModal = () => {
    setIsTptModalOpen(false);
    setOrderToUpdateTpt(null);
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  // Fetch orders function
  const fetchOrders = async () => {
    try {
      const response = await fetch(
        buildApiUrl("/api/orderItem/get"),
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch orders");
      }

      const data = await response.json();
      setOrders(data.orders);

      const initialExpandedState = data.orders.reduce((acc, order) => {
        acc[order._id] = false;
        return acc;
      }, {});

      setExpandedItems(initialExpandedState);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Polling mechanism to check for updates every 5 seconds
  useEffect(() => {
    // Fetch orders immediately when the component mounts
    fetchOrders();

    // Set up polling every 5 seconds
    const intervalId = setInterval(() => {
      console.log("Polling for new orders...");
      fetchOrders(); // Fetch orders every 5 seconds
    }, 1000 * 60);

    // Cleanup the interval when component is unmounted
    return () => {
      clearInterval(intervalId);
    };
  }, [token]);

  const getActionClass = (status) => {
    switch (status?.toLowerCase()) {
      case "pending":
        return "bg-yellow-200 text-yellow-800";
      case "accept":
        return "bg-green-200 text-green-800";
      case "reject":
        return "bg-red-200 text-red-800";
      default:
        return "bg-gray-200 text-gray-800";
    }
  };

  const toggleItemRow = (orderId) => {
    setExpandedItems((prevState) => {
      // If the clicked modal is already open, close it by returning an empty object.
      if (prevState[orderId]) {
        return {};
      }
      // Otherwise, open only this modal.
      return { [orderId]: true };
    });
  };

  const openEditModal = (order) => {
    setOrderToEdit(order);
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setOrderToEdit(null);
  };

  const handleOrderCreated = (newOrder) => {
    setOrders((prevOrders) => [newOrder, ...prevOrders]);
  };

  const handleOrderUpdated = (response) => {
    // Extract the orderItem from the response
    const updatedOrder = response.orderItem || response;
    
    // Update the order in the list
    setOrders((prevOrders) =>
      prevOrders.map((order) =>
        order._id === updatedOrder._id ? updatedOrder : order
      )
    );
    
    // Show success toast message
    toast.success("Order updated successfully!", {
      position: "top-right",
      autoClose: 3000,
    });
  };

  const handleUpdateStatus = async (orderId, newStatus) => {
    // Find the order to check if amount is set
    const order = orders.find((o) => o._id === orderId);
    
    // Prevent status update if amount is not set (except for Pending status)
    if (order && order.totalItemAmount <= 0 && newStatus !== "Pending") {
      toast.error("Cannot update status. Total amount is not set yet.");
      return;
    }

    try {
      const response = await fetch(
        buildApiUrl(`/api/orderItem/accept-reject/${orderId}`),
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ status: newStatus }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Failed to update status");
      }

      // Update the order in the state
      setOrders((prevOrders) =>
        prevOrders.map((order) =>
          order._id === orderId ? { ...order, status: newStatus } : order
        )
      );

      // Show a success toast
      toast.success("Order status updated successfully!");
    } catch (error) {
      // Show an error toast
      toast.error("Error: " + error.message);
    }
  };

  const handleDeleteOrder = async (orderId) => {
    const token = localStorage.getItem("token");

    if (!orderId || !token) {
      toast.error("Order ID or token is missing.");
      return;
    }

    // Show confirmation dialog using Swal
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    // If the user confirms, proceed with the deletion
    if (result.isConfirmed) {
      try {
        const response = await fetch(
          buildApiUrl(`/api/orderItem/${orderId}`),
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to delete order");
        }

        setOrders((prevOrders) =>
          prevOrders.filter((order) => order._id !== orderId)
        );
        toast.success("Order deleted successfully!");
      } catch (error) {
        toast.error("Error: " + error.message);
      }
    } else {
      // If the user cancels, show a cancel message
      toast.info("Order deletion canceled.");
    }
  };

  const filteredByTab = orders.filter((order) => {
    console.log("order tpt status", order.tpt?.tptStatus);
    if (selectedTab === "all") return true;
    if (selectedTab === "accept") {
      return (
        order.status === "Accept" && order.orderDeliveredStatus != "Dispatched"
      );
    }
    if (selectedTab === "waitingForTpt") {
      return (
        order.status === "Accept" &&
        order.orderDeliveredStatus === "Dispatched" &&
        order.tpt?.tptStatus === "Pending"
      );
    }

    if (selectedTab === "dispatched") {
      return (
        order.status === "Accept" &&
        order.orderDeliveredStatus === "Dispatched" &&
        order.tpt?.tptStatus !== "Pending"
      );
    }

    return order.status.toLowerCase() === selectedTab.toLowerCase();
  });

  // Filter orders by search query
  const filteredOrders = filteredByTab.filter((order) => {
    const queryLower = searchQuery.toLowerCase();

    return (
      order._id?.toLowerCase().includes(queryLower) ||
      order.status?.toLowerCase().includes(queryLower) ||
      order.items?.some(
        (item) =>
          item.categoryName?.toLowerCase().includes(queryLower) ||
          item.itemName?.toLowerCase().includes(queryLower)
      )
    );
  });

  // Sorting orders by status: Pending, Reject, Accept
  const sortedOrders = filteredOrders.sort((a, b) => {
    const statusOrder = ["Pending", "Reject", "Accept"];
    const statusComparison =
      statusOrder.indexOf(a.status) - statusOrder.indexOf(b.status);
    if (statusComparison === 0) {
      return new Date(b.createdAt) - new Date(a.createdAt);
    }
    return statusComparison;
  });

  const orderCounts = {
    all: orders.length,
    pending: orders.filter((order) => order.status?.toLowerCase() === "pending")
      .length,
    accept: orders.filter(
      (order) =>
        order.status?.toLowerCase() === "accept" &&
        order.orderDeliveredStatus != "Dispatched"
    ).length,
    reject: orders.filter((order) => order.status?.toLowerCase() === "reject")
      .length,
    waitingForTpt: orders.filter(
      (order) =>
        order.status === "Accept" &&
        order.orderDeliveredStatus === "Dispatched" &&
        order.tpt?.tptStatus === "Pending"
    ).length,
    dispatched: orders.filter(
      (order) =>
        order.status === "Accept" &&
        order.orderDeliveredStatus === "Dispatched" &&
        order.tpt?.tptStatus !== "Pending"
    ).length,
  };

  // Pagination Logic
  const indexOfLastOrder = currentPage * ordersPerPage;
  const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
  const currentOrders = sortedOrders.slice(indexOfFirstOrder, indexOfLastOrder); // Use sorted orders for pagination

  // Calculate total pages
  const totalPages = Math.ceil(sortedOrders.length / ordersPerPage);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <ColorRing
          visible={true}
          height="120"
          width="120"
          ariaLabel="color-ring-loading"
          wrapperStyle={{}}
          wrapperClass="color-ring-wrapper"
          colors={["#e15b64", "#f47e60", "#f8b26a", "#abbd81", "#849b87"]}
        />
      </div>
    );
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  const handleNavigation = () => {
    if (userRole === "admin") {
      navigate("/admin/order/status-details"); // Admin path
    } else {
      navigate("/sales-executive/order/status-details"); // Sales Executive path
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm">
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      <div
        className=" sm:max-h-[100vh] overflow-y-auto"
        style={{ scrollbarWidth: "none" }}
      >
        <div className=" sticky top-0 z-30 bg-white py-1 sm:shadow-md">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-2">
            <h2 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-extrabold text-gray-900 tracking-tight">
              Orders Dashboard
            </h2>
            <div className="flex space-x-3">
              {userRole === "sales executive" && (
                <button
                  onClick={openModal}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Order
                </button>
              )}

              <button
                onClick={handleNavigation}
                className="inline-flex items-center px-3 py-2 border border-transparent text-xs font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
              >
                <FileText className="w-4 h-4 mr-2" />
                Order Status
              </button>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="flex flex-col sm:flex-row items-center justify-between">
            <div className="flex flex-wrap gap-2 mb-4 sm:mb-0">
              {[
                {
                  id: "all",
                  label: "All",
                  count: orderCounts.all,
                  color: "blue",
                },
                {
                  id: "pending",
                  label: "Pending",
                  count: orderCounts.pending,
                  color: "gray",
                },
                {
                  id: "accept",
                  label: "Accepted",
                  count: orderCounts.accept,
                  color: "green",
                },
                {
                  id: "reject",
                  label: "Reject",
                  count: orderCounts.reject,
                  color: "red",
                },
                {
                  id: "waitingForTpt",
                  label: "Waiting for TPT",
                  count: orderCounts.waitingForTpt,
                  color: "gray",
                },
                {
                  id: "dispatched",
                  label: "All Clear",
                  count: orderCounts.dispatched,
                  color: "green",
                },
              ].map((tab) => (
                <button
                  key={tab.id}
                  className={`py-1 px-2 rounded-lg text-[11px] font-medium ${
                    selectedTab === tab.id
                      ? `bg-${tab.color}-500 text-white`
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  } transition duration-200`}
                  onClick={() => setSelectedTab(tab.id)}
                >
                  {tab.label}
                  <span
                    className={`ml-2 text-[11px] bg-${tab.color}-100 text-${tab.color}-800 rounded-full px-2`}
                  >
                    {tab.count}
                  </span>
                </button>
              ))}
            </div>
            <SearchBar onSearch={(query) => setSearchQuery(query)} />
          </div>
        </div>

        {/* Orders Table */}
        <div className="overflow-x-auto rounded-md">
          <table className="table-auto w-full border-collapse hidden sm:table">
            <tbody>
              {currentOrders.map((order, orderIndex) => (
                <React.Fragment key={order._id}>
                  {/* Order Summary Row */}
                  <tr
                    className={`border-b bg-white hover:bg-gray-100 ${
                      expandedItems[order._id] &&
                      "!bg-red-50  border border-gray-300"
                    }`}
                  >
                    <td colSpan="7" className="p-2">
                      <div className="flex justify-between items-center">
                        <div>
                          <strong className="text-gray-800 uppercase">
                            Order ID:{" "}
                            <span className="font-semibold text-indigo-600 text-[10px] bg-blue-100 p-1 ml-2 uppercase rounded-md">
                              {order._id.slice(-5)}
                            </span>
                          </strong>
                          {order.totalItemAmount === 0 && (
                            <span className="bg-green-500 text-white font-semibold ml-2 p-1 rounded text-[10px]">
                              New Order
                            </span>
                          )}
                          <div className="text-xs text-gray-600 mt-1">
                            Firm Name -{" "}
                            <span className="font-medium">
                              {order.createdBy ? order.createdBy.firmName : ""}
                            </span>{" "}
                            | Created By -{" "}
                            <span className="font-medium">
                              {order.createdBy
                                ? order.createdBy.name
                                : "Unknown"}
                            </span>{" "}
                            | Assigned To -{" "}
                            <span className="font-medium">
                              {order.assignedTo
                                ? order.assignedTo.name
                                : "Not Assigned"}
                            </span>{" "}
                            <br />
                            Total Amount -{" "}
                            <span className="font-medium">
                              ₹{convertToCr(order.totalItemAmount)}
                            </span>{" "}
                            <br /> TransPorter Name -{" "}
                            <span className="font-medium">
                              {order.transporterName}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center space-x-4">
                          <div>
                            <FaFilePdf
                              size={14}
                              className="text-red-600 hover:text-red-800 cursor-pointer"
                            />
                          </div>
                          <div
                            className={`px-3 py-1 font-medium rounded-md text-xs ${getActionClass(
                              order.status
                            )}`}
                          >
                            {order.status}
                          </div>
                          {order.totalItemAmount > 0 ? (
                            <select
                              className="bg-gray-100 border border-gray-200 rounded-lg px-3 py-1 text-xs hover:bg-gray-200 transition duration-200"
                              onChange={(e) =>
                                handleUpdateStatus(order._id, e.target.value)
                              }
                              defaultValue={order.status}
                            >
                              <option value="" disabled>
                                Select Status
                              </option>
                              <option value="Pending">Pending</option>
                              <option value="Accept">Accept</option>
                              <option value="Reject">Reject</option>
                            </select>
                          ) : (
                            <select
                              className="bg-gray-100 border border-gray-200 rounded-lg px-3 py-1 text-xs opacity-50 cursor-not-allowed transition duration-200"
                              disabled
                              defaultValue={order.status}
                            >
                              <option value={order.status} disabled>
                                {order.status} (Amount Not Set)
                              </option>
                            </select>
                          )}
                          {order.orderDeliveredStatus === "Dispatched" && (
                            <button
                              onClick={() => openTptUpdateModal(order._id)}
                              className="text-green-500 hover:text-green-700 flex items-center"
                            >
                              <FaShippingFast size={14} className="mr-2" />{" "}
                              Update
                            </button>
                          )}
                          {userRole === "sales executive" && (
                            <button
                              onClick={() => openEditModal(order)}
                              className="text-blue-500 hover:text-blue-700"
                            >
                              <FaEdit size={14} />
                            </button>
                          )}
                          {userRole === "admin" && (
                            <button
                              onClick={() => handleDeleteOrder(order._id)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <FaTrash size={14} />
                            </button>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>

                  {/* Serial Number Row */}
                  <tr
                    className={`border-b border-gray-300 bg-white hover:bg-gray-100 ${
                      expandedItems[order._id] &&
                      "!bg-red-50 border border-gray-300"
                    }`}
                  >
                    <td className="px-2 py-1">{orderIndex + 1}</td>
                    <td colSpan="6">
                      <button
                        onClick={() => toggleItemRow(order._id)}
                        className="text-indigo-600 hover:text-indigo-800 text-xs font-medium transform hover:scale-105 transition duration-200"
                      >
                        {expandedItems[order._id]
                          ? "Hide Details"
                          : "Show Details"}
                      </button>
                    </td>
                  </tr>

                  {/* Expanded Item Rows */}
                  {expandedItems[order._id] && (
                    <tr className="border border-gray-300">
                      <td colSpan="7" className="p-0">
                        <table className="table-auto w-full border-collapse">
                          <thead>
                            <tr className="text-left bg-gray-100 text-sm text-gray-600">
                              <th className="px-2 py-1 font-medium">#</th>
                              <th className="px-2 py-1 font-medium">
                                Category
                              </th>
                              <th className="px-2 py-1 font-medium">
                                Product Items
                              </th>
                              <th className="px-2 py-1 font-medium">
                                Quantity
                              </th>
                              <th className="px-2 py-1 font-medium">
                                Warehouse Name
                              </th>
                              <th className="px-2 py-1 font-medium">
                                Warehouse Batch
                              </th>
                              <th className="px-2 py-1 font-medium">Price</th>
                              <th className="px-2 py-1 font-medium">
                                Total Amount
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {order.items.map((item, itemIndex) => (
                              <tr key={item._id} className={`border-b text-sm`}>
                                <td className="px-2 py-0.5">{itemIndex + 1}</td>
                                <td className="px-2 py-0.5">
                                  {capitalizeWords(item.categoryName)}
                                </td>
                                <td className="px-2 py-0.5">
                                  {capitalizeWords(item.itemName)}
                                </td>
                                <td className="px-2 py-0.5">{item.qty}</td>
                                <td className="px-2 py-0.5">
                                  {capitalizeWords(item.wareHouseName)}
                                </td>
                                <td className="px-2 py-0.5">
                                  {item.inwardReference || "-"}
                                </td>
                                <td className="px-2 py-0.5">
                                  {item.price ? `₹${item.price}` : "-"}
                                </td>
                                <td className="px-2 py-0.5">
                                  {item.totalAmount
                                    ? `₹${convertToCr(item.totalAmount)}`
                                    : "-"}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>

          {/* Mobile View */}
          <div className="block sm:hidden">
            {currentOrders.map((order) => (
              <MobileOrderCard
                key={order._id}
                order={order}
                getActionClass={getActionClass}
                openEditModal={openEditModal}
                handleDeleteOrder={handleDeleteOrder}
                handleUpdateStatus={handleUpdateStatus}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        setCurrentPage={setCurrentPage}
      />

      {/* Modals */}
      {isModalOpen && (
        <ForClientOrderModal isOpen={isModalOpen} closeModal={closeModal} />
      )}
      {isEditModalOpen && (
        <EditOrderModal
          order={orderToEdit}
          closeModal={closeEditModal}
          onOrderUpdated={handleOrderUpdated}
        />
      )}
      {isTptModalOpen && (
        <TptUpdateModal
          isOpen={isTptModalOpen}
          closeModal={closeTptUpdateModal}
          orderId={orderToUpdateTpt}
        />
      )}
    </div>
  );
};

export default SalesOrdersTable;
